import {
  type ActionWithDependencies,
  commonReducers,
  onBatchEnd,
  type SimpleStore,
} from '@databases/utils/redux/redux';
import { type EnhancedStore, type MiddlewareArray } from '@reduxjs/toolkit';
import { isEqual } from 'lodash-es';
import { useContext, useDebugValue, useMemo, useRef, useState } from 'react';
import {
  type EqualityFn,
  ReactReduxContext,
  shallowEqual,
  useDispatch,
  useStore,
} from 'react-redux';
import { type AnyAction, type Middleware, type StateFromReducersMapObject } from 'redux';
import { type ThunkMiddleware } from 'redux-thunk';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/with-selector';

import cellSelectionsSlice from './cellSelections/slice';
import changesetsSlice from './changesets/slice';
import connectionsSlice from './connections/slice';

export const defaultReducers = {
  ...commonReducers,
  cellSelections: cellSelectionsSlice.reducer,
  changesets: changesetsSlice.reducer,
  connections: connectionsSlice.reducer,
};

export type DefaultRootState = StateFromReducersMapObject<typeof defaultReducers>;
export type DefaultStore = EnhancedStore<
  DefaultRootState,
  AnyAction,
  MiddlewareArray<
    [ThunkMiddleware<DefaultRootState>, Middleware<object, DefaultRootState>]
  >
>;
export const defaultStoreUtils = getAppStoreUtils<DefaultRootState>();

/**
 * Redux helper functions and hooks typed for a specific root state.
 */
export interface AppStoreUtils<S extends SimpleStore<any>> {
  useDispatch: () => S['dispatch'];

  /**
   * Selects a value from the redux store.
   */
  useSelector: <R>(selector: (state: ReturnType<S['getState']>) => R) => R;

  /**
   * Like useSelector, but uses a deep equality check to determine if the result has changed.
   */
  useEqualSelector: <R>(selector: (state: ReturnType<S['getState']>) => R) => R;

  /**
   * Like useSelector, but uses a shallow equality check to determine if the result has changed.
   */
  useShallowEqualSelector: <R>(selector: (state: ReturnType<S['getState']>) => R) => R;

  useStore: () => S;
}

/**
 * Wraps the given Redux subscription listener to be delayed while a Redux batch operation is running.
 * To be used with store.subscribe() or subscription.addNestedSub().
 */
export function batchedSubscriptionListener(callback: () => void): () => void {
  let isPending = false;
  return () => {
    if (!isPending) {
      isPending = true;
      onBatchEnd(() => {
        isPending = false;
        callback();
      });
    }
  };
}

/**
 * Like react-redux useSelector(), but selectors are not executed while a Redux batch operation is running.
 */
function useBatchedSelector<TState, Selected>(
  selector: (state: TState) => Selected,
  equalityFn?: EqualityFn<Selected> | undefined
): Selected {
  const context = useContext(ReactReduxContext);
  if (!context) {
    throw new Error(
      'could not find react-redux context value; please ensure the component is wrapped in a <Provider>'
    );
  }

  //const _debug_component = getComponentNameFromStack(new Error().stack!);

  const selectedState = useSyncExternalStoreWithSelector(
    (listener) =>
      context.subscription.addNestedSub(batchedSubscriptionListener(listener)),
    context.store.getState,
    context.store.getState,
    (...args) => {
      return selector(...args);
    },
    equalityFn
  );

  useDebugValue(selectedState);

  return selectedState;
}

export function getAppStoreUtils<S>(): AppStoreUtils<
  EnhancedStore<
    S,
    AnyAction,
    MiddlewareArray<[ThunkMiddleware<S>, Middleware<object, S>]>
  >
> {
  return {
    useDispatch,
    useSelector: (selector) => useBatchedSelector(selector),
    useEqualSelector: (selector) => useBatchedSelector(selector, isEqual),
    useShallowEqualSelector: (selector) => useBatchedSelector(selector, shallowEqual),
    useStore: useStore as any,
  };
}

/**
 * Runs the given action, tracking all the selectors that it calls wrapped in helpers.equalsSelector() and rerunning
 * the action when any selector result (or the action itself) changes.
 * {@see ActionWithDependencies}
 */
export function useActionWithDependencies<R>(
  action: ActionWithDependencies<DefaultStore, R>
): { isDispatching: boolean; result: R } {
  const selectorsRef = useRef<Array<(state: DefaultRootState) => any>>([]);

  const selectorResult = defaultStoreUtils.useEqualSelector((state) => {
    return selectorsRef.current.map((selector) => selector(state));
  });

  const runningDispatches = useRef(0);
  const [isDispatching, setIsDispatching] = useState(false);

  const store = defaultStoreUtils.useStore();
  const result = useMemo(() => {
    selectorResult;
    selectorsRef.current = [];
    const thisSelectors = selectorsRef.current;

    return action({
      dispatch: async (...args: any[]) => {
        // Defer this, since we should not dispatch state updates synchronously from inside useMemo()
        await Promise.resolve();
        // eslint-disable-next-line prefer-spread
        const result = store.dispatch.apply(store, args as any) as any;
        if (typeof result === 'object' && 'then' in result) {
          runningDispatches.current++;
          Promise.resolve()
            .then(() => {
              setIsDispatching(true);
              return result;
            })
            .finally(() => {
              setIsDispatching(--runningDispatches.current <= 0);
            });
        }
      },
      selector: (selector) => {
        thisSelectors.push(selector);
        return selector(store.getState());
      },
    });
  }, [action, selectorResult, store]);

  return { isDispatching, result };
}
